import { useLayoutEffect, useRef, useState } from "react";
import ChartCard from "../../../components/ui/ChartCard";
import { buildStackedBarOptions } from "../chartOptions";
import { buildLikertPeriodChart } from "./resultHelpers";
import { TextResponsePanel } from "./MultiChoiceWithTextPanel";
import StructuredPeriodAxis from "./StructuredPeriodAxis";

function formatPercentage(value) {
  const number = Number(value) || 0;
  return Number.isInteger(number) ? `${number}%` : `${number.toFixed(1)}%`;
}

function formatIntegerPercentage(value) {
  return `${Math.round(Number(value) || 0)}%`;
}

export default function LoonOpZandBinaryStackedPanel({
  question,
  textQuestion,
  filters,
  colors: suppliedColors,
  textPanelTitle = "Anders, namelijk (ander soort hulp):",
  info,
  excludeUnansweredIssues = false,
}) {
  const chartContainerRef = useRef(null);
  const [panelHeight, setPanelHeight] = useState(null);
  const selectedIssues = filters?.selected_issues || [];
  const issues = excludeUnansweredIssues
    ? selectedIssues.filter((issue) =>
        question.options.some((option) => {
          const value = option.by_issue?.find((item) => item.issue === issue);
          return Number(value?.count) > 0;
        })
      )
    : selectedIssues;
  const issueLabels = new Map(
    (filters?.available_issues || []).map((issue) => [issue.value, issue.label])
  );
  const chart = buildLikertPeriodChart(
    [question],
    { ...filters, selected_issues: issues }
  );
  const colors = suppliedColors || chart.series.map((series) =>
    String(series.name).trim().toLowerCase() === "ja" ? "#22c55e" : "#ef4444"
  );
  const options = buildStackedBarOptions({
    categories: chart.categories,
    colors,
    horizontal: true,
    stackType: "100%",
    dataLabels: true,
    legendPosition: "bottom",
    tooltipFormatter: (_value, context) => {
      const detail = chart.details?.[context.seriesIndex]?.[context.dataPointIndex];
      const issue = issues[context.dataPointIndex];
      return detail
        ? `${detail.count} respondenten · ${formatPercentage(
            detail.percentage
          )} · ${issueLabels.get(issue) || issue}`
        : "";
    },
  });

  options.chart.animations = { enabled: false };
  options.plotOptions.bar.barHeight = "68%";
  options.xaxis = {
    ...options.xaxis,
    min: 0,
    max: 100,
    tickAmount: 5,
    labels: { formatter: (value) => `${Math.round(Number(value))}%` },
  };
  options.yaxis = { labels: { show: false } };
  options.dataLabels = {
    enabled: true,
    formatter: (_value, context) => {
      const detail = chart.details?.[context.seriesIndex]?.[context.dataPointIndex];
      return detail && detail.percentage >= 8
        ? formatIntegerPercentage(detail.percentage)
        : "";
    },
    style: { fontSize: "11px", fontWeight: 600, colors: ["#111827"] },
    dropShadow: { enabled: false },
  };
  options.legend = { ...options.legend, fontSize: "13px" };
  options.tooltip = { shared: false, intersect: false, y: options.tooltip?.y };
  options.grid = { padding: { left: 0, right: 20, bottom: 14 } };

  const height = Math.max(270, issues.length * 38 + 120);

  useLayoutEffect(() => {
    const chartContainer = chartContainerRef.current;
    if (!chartContainer) {
      return undefined;
    }
    const syncHeight = () => {
      setPanelHeight(Math.round(chartContainer.getBoundingClientRect().height));
    };
    syncHeight();
    const resizeObserver = new ResizeObserver(syncHeight);
    resizeObserver.observe(chartContainer);
    return () => resizeObserver.disconnect();
  }, []);

  const chartCard = (
    <div ref={chartContainerRef} className="loonopzand-dashboard__choice-chart">
      <ChartCard
        chartKey={`loonopzand-binary-${question.question_id}-${issues.join("-")}`}
        title={question.text}
        info={info || `${question.answered_count} van ${question.response_count} respondenten beantwoordden deze vraag.`}
        type="bar"
        options={options}
        series={chart.series}
        height={height}
        chartSideContent={(
          <StructuredPeriodAxis
            periodOnly
            groups={[{ key: question.question_id, label: "" }]}
            issues={issues}
          />
        )}
      />
    </div>
  );

  if (!textQuestion) {
    return chartCard;
  }

  return (
    <div className="loonopzand-dashboard__choice-with-text">
      {chartCard}
      <TextResponsePanel
        question={textQuestion}
        availableIssues={filters?.available_issues || []}
        height={panelHeight}
        title={textPanelTitle}
      />
    </div>
  );
}
